import { useMemo, useState } from "react";
import { ArrowLeft, HandHeart, Loader2, Send, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageTitle, useApi, Skeleton, Stagger, Item, badge, inputCls, btnPrimary } from "@/components/portal/ui";

const STATUS = { pending: ["warn", "Menunggu persetujuan"], approved: ["success", "Diterima melayani"], rejected: ["danger", "Belum diproses"] };
const AVAILABILITY = ["Ibadah Raya 1 (pagi)", "Ibadah Raya 2 (siang)", "Ibadah Youth", "Sekolah Minggu", "Hari kerja / weekday", "Fleksibel"];

export default function ServeUp() {
  const { data, reload } = useApi("/portal/ministry/categories");
  const { data: formDef } = useApi("/portal/ministry/form");
  const [pick, setPick] = useState(null);
  const [form, setForm] = useState({ role: "", availability: "", experience: "", note: "", phone: "" });
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);

  const roles = useMemo(() => pick?.roles || [], [pick]);
  const close = () => { setPick(null); setAnswers({}); setForm({ role: "", availability: "", experience: "", note: "", phone: "" }); };

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/portal/ministry/serve-up", { ministry_id: pick.id, ...form, answers });
      toast.success("Terima kasih! Pendaftaran dikirim ke tim pastoral.");
      close();
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="portal-serve-up">
      <div className="px-5 pt-10 flex items-center gap-3">
        <Link to="/portal" data-testid="serve-up-back" className="w-10 h-10 rounded-full bg-white border border-slate-200 grid place-items-center active:scale-95 transition"><ArrowLeft className="w-5 h-5" /></Link>
        <p className="text-[11px] font-bold uppercase tracking-widest text-blue-600">Rindu Melayani</p>
      </div>
      <PageTitle eyebrow="Serve-Up" title="Ayo Melayani" />
      <div className="px-5 mt-2">
        <div className="rounded-3xl bg-slate-900 text-white p-5" data-testid="serve-up-hero">
          <HandHeart className="w-6 h-6 text-amber-300" />
          <p className="mt-3 font-extrabold text-lg">Setiap orang dipanggil melayani</p>
          <p className="mt-1 text-sm text-white/70">Pilih kategori pelayanan yang sesuai karunia Anda. Tim pastoral akan meninjau dan menghubungi Anda.</p>
        </div>
      </div>

      <div className="px-5 mt-5">
        {!data ? <Skeleton className="h-80" /> : (
          <Stagger className="space-y-3">
            {data.applications?.length > 0 && (
              <Item><p className="px-1 text-sm font-bold text-slate-900">Pendaftaran Saya</p></Item>
            )}
            {data.applications?.map((a) => {
              const [tone, label] = STATUS[a.status] || ["info", a.status];
              return (
                <Item key={a.id} className="bg-white rounded-2xl p-4 border border-slate-100" data-testid={`serve-app-${a.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0"><p className="font-bold text-slate-900 truncate">{a.ministry_name}</p><p className="text-xs text-slate-500">{a.role || "Role menyesuaikan"}{a.availability ? ` · ${a.availability}` : ""}</p></div>
                    <span className={`shrink-0 text-[11px] font-bold rounded-full px-3 py-1 ${badge[tone]}`}>{label}</span>
                  </div>
                  {a.admin_note && <p className="mt-2 text-xs text-slate-500">Catatan: {a.admin_note}</p>}
                </Item>
              );
            })}

            <Item><p className="px-1 mt-2 text-sm font-bold text-slate-900">Kategori Pelayanan</p></Item>
            {data.categories.map((c) => {
              const joined = data.my_ministry_ids.includes(c.id);
              return (
                <Item key={c.id}>
                  <button onClick={() => { setPick(c); setForm((f) => ({ ...f, role: "" })); }} data-testid={`serve-category-${c.id}`}
                    className="w-full text-left bg-white rounded-2xl p-4 border border-slate-100 hover:border-blue-300 active:scale-[0.99] transition flex gap-3 items-start">
                    <span className="w-10 h-10 shrink-0 rounded-xl bg-blue-50 text-blue-600 grid place-items-center"><Users className="w-5 h-5" /></span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2"><b className="text-slate-900">{c.name}</b>{joined && <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${badge.success}`}>Aktif</span>}</span>
                      <span className="block text-xs text-slate-500 mt-0.5">{c.description}</span>
                      {c.roles.length > 0 && <span className="block text-[11px] text-slate-400 mt-1">{c.roles.slice(0, 4).join(" · ")}</span>}
                    </span>
                  </button>
                </Item>
              );
            })}
          </Stagger>
        )}
      </div>

      {pick && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6" data-testid="serve-form-sheet" onClick={close}>
          <div className="w-full max-w-md bg-white rounded-t-3xl md:rounded-3xl p-5 space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <p className="font-extrabold text-slate-900">{pick.name}</p>
            <p className="text-xs text-slate-500">{pick.description}</p>
            {roles.length > 0 ? (
              <select className={inputCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="serve-role-select">
                <option value="">Pilih role…</option>{roles.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            ) : (
              <input className={inputCls} placeholder="Role yang diminati (opsional)" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="serve-role-input" />
            )}
            <select className={inputCls} value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })} data-testid="serve-availability-select">
              <option value="">Ketersediaan waktu…</option>{AVAILABILITY.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <input className={inputCls} placeholder="Nomor WhatsApp (mis. 0812xxxx)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="serve-phone-input" />
            {(formDef?.questions || []).map((q) => {
              const set = (val) => setAnswers((a) => ({ ...a, [q.id]: val }));
              const val = answers[q.id] || "";
              return (
                <label key={q.id} className="block" data-testid={`serve-question-${q.id}`}>
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{q.label}{q.required && " *"}</span>
                  {q.type === "textarea" ? (
                    <textarea className={`${inputCls} mt-1.5 min-h-20`} placeholder={q.placeholder} value={val} onChange={(e) => set(e.target.value)} />
                  ) : q.type === "select" ? (
                    <select className={`${inputCls} mt-1.5`} value={val} onChange={(e) => set(e.target.value)}>
                      <option value="">Pilih…</option>{q.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input className={`${inputCls} mt-1.5`} placeholder={q.placeholder} value={val} onChange={(e) => set(e.target.value)} />
                  )}
                </label>
              );
            })}
            <textarea className={`${inputCls} min-h-16`} placeholder="Catatan untuk tim pastoral (opsional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="serve-note-input" />
            <div className="flex gap-2 pt-1">
              <button onClick={close} data-testid="serve-cancel-button" className="flex-1 rounded-xl border border-slate-200 py-3 font-bold text-slate-700 text-sm">Batal</button>
              <button onClick={submit} disabled={saving} data-testid="serve-submit-button" className={`${btnPrimary} flex-1 !py-3`}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" />Kirim</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
